// SPDX-License-Identifier: AGPL-3.0-or-later

import {resolveActiveHolidayThemePreset} from '@app/features/holiday/state/HolidayThemePresets';
import {nextAnyHolidayPhaseBoundary} from '@app/features/holiday/utils/holidaySeason';
import {clearRemScaleCache} from '@app/features/theme/layout/RemFromPx';
import {invalidateAvatarCostumeSurfaceColor} from '@app/features/ui/avatars/AvatarCostume';
import {useEffect} from 'react';

const STYLE_ELEMENT_ID = 'fluxer-holiday-theme-style';
const CUSTOM_THEME_STYLE_ELEMENT_ID = 'fluxer-custom-theme-style';

/**
 * `setTimeout` overflows past ~24.8 days and fires immediately, and Halloween's next boundary can
 * be ten months out. Re-arming on a ceiling also recovers from a machine resuming from sleep,
 * where a long pending timer fires late or not at all.
 */
const MAX_BOUNDARY_DELAY_MS = 6 * 60 * 60 * 1000;

function removeHolidayThemeStyle(): void {
	const existing = document.getElementById(STYLE_ELEMENT_ID);
	if (existing?.parentNode) {
		existing.parentNode.removeChild(existing);
		clearRemScaleCache();
		invalidateAvatarCostumeSurfaceColor();
	}
}

function applyHolidayThemeStyle(css: string): void {
	const existing = document.getElementById(STYLE_ELEMENT_ID) as HTMLStyleElement | null;
	const styleElement = existing ?? document.createElement('style');
	styleElement.id = STYLE_ELEMENT_ID;
	if (styleElement.textContent !== css) {
		styleElement.textContent = css;
	}
	if (!existing) {
		// Must sit before the user's own CSS: both declare `:root { … }`, so equal specificity means
		// source order decides, and a shipped preset must never beat something the user wrote.
		const customThemeStyle = document.getElementById(CUSTOM_THEME_STYLE_ELEMENT_ID);
		if (customThemeStyle?.parentNode) {
			customThemeStyle.parentNode.insertBefore(styleElement, customThemeStyle);
		} else {
			document.head.appendChild(styleElement);
		}
	}
	clearRemScaleCache();
	// The preset moves every surface colour, but none of the avatar halo cache key inputs, so the
	// probe has to be dropped explicitly now that the style element is actually in the DOM.
	invalidateAvatarCostumeSurfaceColor();
}

/**
 * Applies the in-season holiday palette as its own style element.
 *
 * Kept out of `useCustomThemeStyle` on purpose: preset CSS is app-shipped, not user content, so it
 * must not reach `Accessibility`'s sync payload or Theme Studio's "copy full CSS" output.
 *
 * Call this **before** `useCustomThemeStyle` — effects run in call order, so the holiday element
 * gets appended first and lands in the right cascade position.
 *
 * Must be called from an `observer` component: `resolveActiveHolidayThemePreset` reads
 * `HolidayPreferences` observables, and without tracking the palette won't react to the toggle.
 */
export function useHolidayThemeStyle(): void {
	const preset = resolveActiveHolidayThemePreset();
	const css = preset?.css ?? null;
	useEffect(() => {
		if (css == null) {
			removeHolidayThemeStyle();
		} else {
			applyHolidayThemeStyle(css);
		}
		return () => {
			removeHolidayThemeStyle();
		};
	}, [css]);
	useEffect(() => {
		if (typeof window === 'undefined') return;
		let timer: ReturnType<typeof setTimeout> | null = null;
		let cancelled = false;
		// Re-resolving on a boundary is what makes the palette appear and vanish without a reload.
		// `forceRender` is not needed — reading the observable above re-runs the component, and this
		// timer only has to nudge it once the calendar moves.
		const tick = () => {
			if (cancelled) return;
			const now = new Date();
			const delay = Math.min(
				Math.max(nextAnyHolidayPhaseBoundary(now).getTime() - now.getTime(), 1000),
				MAX_BOUNDARY_DELAY_MS,
			);
			timer = setTimeout(() => {
				const active = resolveActiveHolidayThemePreset();
				if (active == null) {
					removeHolidayThemeStyle();
				} else {
					applyHolidayThemeStyle(active.css);
				}
				tick();
			}, delay);
		};
		tick();
		return () => {
			cancelled = true;
			if (timer != null) clearTimeout(timer);
		};
	}, []);
}
